import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs, useFlags } from 'wsm-common-data';
import useEventBus from './useEventBus';
import {
	addPaymentTimeoutToNewRelic,
	addTotalTimeToNewRelic,
	addPaymentRenderToNewRelic,
	addPaymentMismatchToNewRelic,
	comparePaymentData,
	startTimeMarker,
	endTimeMarker,
	getPerformanceMeasure
} from '../helpers/logging-client';
import { ESNTIAL_PAYMENT_ERROR_MESSAGE } from '../constants';
import useErrorHandler from './useErrorHandler';
import {
	extractVehicleUUID,
	getCachedPaymentData
} from '../helpers/payment-cache-utils';

// Payment type labels and display order
const PAYMENT_TYPE_CONFIG = {
	paymentOffers: { label: 'Finance', displayIndex: 0 },
	leaseOffers: { label: 'Lease', displayIndex: 1 },
	cashOffers: { label: 'Cash', displayIndex: 2 }
};

const DEAL_TYPE_CONFIG = {
	finance: 'paymentOffers',
	lease: 'leaseOffers',
	cash: 'cashOffers'
};

const usePaymentData = () => {
	const { enableIncentives } = usePrefs();
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [udsPayments, setUdsPayments] = useState(null);
	const [paymentsData, setPaymentsData] = useState(null);
	const listingType = useSelector((state) => state.listingType);
	const [eventBus, isReady] = useEventBus();
	const paymentsRef = useRef(udsPayments);
	const errorTimeoutRef = useRef(null);
	const cachedPaymentDataRef = useRef(null); // Track cached data for comparison
	const hasRenderedCachedRef = useRef(false); // Prevent duplicate cached renders
	const flags = useFlags();
	const paymentTimeout =
		flags['ws-track-my-offer-uds-payment-timeout'] || 10000;

	const hideOnEsntialPaymentError = useErrorHandler(
		setHasError,
		setIsLoading,
		ESNTIAL_PAYMENT_ERROR_MESSAGE
	);

	// Move handlePaymentUpdate outside useEffect so it can be used in caching logic
	const handlePaymentUpdate = (paymentInfo, isFromCache = false) => {
		endTimeMarker('payment');
		const elapsedTime = getPerformanceMeasure('payment');

		logger.info(
			`DDC: WS-TRACK-MY-OFFER -> uds:PaymentCalcResponse:Update: ${JSON.stringify(
				paymentInfo,
				null,
				2
			)}`
		);

		// Track payment render and handle mismatch detection
		if (isFromCache) {
			cachedPaymentDataRef.current = paymentInfo;
			hasRenderedCachedRef.current = true;
		} else if (
			hasRenderedCachedRef.current &&
			cachedPaymentDataRef.current
		) {
			// Check for mismatch if we previously rendered cached data
			const mismatchData = comparePaymentData(
				cachedPaymentDataRef.current,
				paymentInfo
			);
			if (mismatchData) {
				addPaymentMismatchToNewRelic(mismatchData);
			}
		}

		// Track payment render (cacheHit flag set automatically based on renderSource)
		addPaymentRenderToNewRelic(
			isFromCache ? 'cache' : 'fresh',
			elapsedTime
		);

		// Only add total time for fresh renders to avoid double-counting
		if (!isFromCache) {
			addTotalTimeToNewRelic(elapsedTime, 'payment');
		}

		// Clear the error timeout since we received payment data
		if (errorTimeoutRef.current) {
			logger.info(
				'WS-TRACK-MY-OFFER: Clearing error timeout - payment data received',
				{
					hadTimeout: true,
					paymentInfo: paymentInfo ? Object.keys(paymentInfo) : null
				}
			);
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		} else {
			logger.info(
				'WS-TRACK-MY-OFFER: Payment data received (no timeout to clear)',
				{
					paymentInfo: paymentInfo ? Object.keys(paymentInfo) : null
				}
			);
		}

		setUdsPayments(paymentInfo);
		setIsLoading(false);
		const hasPaymentError =
			!!paymentInfo?.paymentErrorState &&
			!paymentInfo?.isFinanceErrorState &&
			!paymentInfo?.isLeaseErrorState;
		if (hasPaymentError) {
			logger.error('WS-TRACK-MY-OFFER: Payment error state detected', {
				paymentErrorState: paymentInfo?.paymentErrorState,
				paymentInfo
			});
		}
		setHasError(hasPaymentError);
	};

	// Simple check for cached payment data
	useEffect(() => {
		if (isBrowser && flags['ws-track-my-offer-preload-payment-data']) {
			try {
				const uuid = extractVehicleUUID(window.location.pathname);
				const paymentData = getCachedPaymentData(uuid);

				if (paymentData) {
					logger.info(
						'WS-TRACK-MY-OFFER: Found cached payment data in localStorage, using immediately',
						{ uuid }
					);

					// Use cached data immediately - mark as from cache
					handlePaymentUpdate(paymentData, true);
				}
			} catch (e) {
				// Ignore errors, continue normally
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags]);

	useEffect(() => {
		const pubsub = window?.DDC?.pubsub;
		if (isReady) {
			logger.info(
				'WS-TRACK-MY-OFFER: usePaymentData setting up event listeners',
				{
					isReady,
					flags: {
						preloadEnabled:
							flags['ws-track-my-offer-preload-payment-data'],
						paymentTimeout
					}
				}
			);

			startTimeMarker('payment');

			// Handle timeout based on feature flag
			errorTimeoutRef.current = setTimeout(() => {
				if (flags['ws-track-my-offer-preload-payment-data']) {
					// New behavior: just log warning, don't hide widget
					logger.warn(
						'WS-TRACK-MY-OFFER: Payment data is taking longer than expected',
						{
							timeout: paymentTimeout,
							currentPayments: paymentsRef.current
						}
					);
				} else {
					// Old behavior: set error state to hide widget
					logger.error(
						'WS-TRACK-MY-OFFER: Payment data timeout reached',
						{
							timeout: paymentTimeout,
							currentPayments: paymentsRef.current
						}
					);
					setHasError(true);
					setIsLoading(false);
				}
				// Always track the timeout for monitoring
				addPaymentTimeoutToNewRelic(paymentTimeout);
				errorTimeoutRef.current = null;
			}, paymentTimeout);

			// For normal UDS / AMD flow
			eventBus.on('uds:PaymentCalcResponse:Update', handlePaymentUpdate);

			// additional event for uds payment update in esntial flow
			// since UDS doesn't calculate payment for esntial, we use
			// uds:paymentUpdate:completed event
			// data model is the same except for the rates key
			// which is handled in the useEffect below
			eventBus.on('uds:paymentUpdate:completed', handlePaymentUpdate);

			// Handle payment errors from esntial-uds-lite
			const handlePaymentError = (errorData) => {
				logger.error(
					'WS-TRACK-MY-OFFER: Received payment error from UDS-LITE',
					{
						error: errorData?.error,
						context: errorData?.context,
						timestamp: errorData?.timestamp
					}
				);

				// Clear any existing timeout
				if (errorTimeoutRef.current) {
					clearTimeout(errorTimeoutRef.current);
					errorTimeoutRef.current = null;
				}

				// Set error state to hide loader
				setHasError(true);
				setIsLoading(false);

				// Track the error
				if (window.newrelic && window.newrelic.addPageAction) {
					window.newrelic.addPageAction('paymentError', {
						error: errorData?.error || 'Unknown error',
						context: errorData?.context || 'payment',
						source: 'uds-lite'
					});
				}
			};

			eventBus.on('uds:paymentUpdate:error', handlePaymentError);

			eventBus.on('uds:calcConfigUpdate:request', (configInfo) => {
				startTimeMarker('payment');
				logger.info(
					'WS-TRACK-MY-OFFER -> uds:calcConfigUpdate:request:',
					configInfo
				);
			});

			eventBus.on('uds:calcConfigUpdate:completed', (configInfo) => {
				logger.info(
					'WS-TRACK-MY-OFFER -> uds:calcConfigUpdate:completed:',
					configInfo
				);
			});

			pubsub?.subscribe(
				'ddc-wallet-integration/esntial:paymentError',
				hideOnEsntialPaymentError
			);

			// Also listen for window event from esntial-uds-lite (backward compatibility)
			const handleWindowPaymentError = (event) => {
				if (event.detail) {
					handlePaymentError(event.detail);
				}
			};

			window.addEventListener(
				'uds:payment:error',
				handleWindowPaymentError
			);

			return () => {
				eventBus.off(
					'uds:PaymentCalcResponse:Update',
					handlePaymentUpdate
				);
				eventBus.off(
					'uds:paymentUpdate:completed',
					handlePaymentUpdate
				);
				eventBus.off('uds:paymentUpdate:error', handlePaymentError);
				pubsub?.unsubscribe(
					'ddc-wallet-integration/esntial:paymentError',
					hideOnEsntialPaymentError
				);
				if (errorTimeoutRef.current) {
					clearTimeout(errorTimeoutRef.current);
					errorTimeoutRef.current = null;
				}
				window.removeEventListener(
					'uds:payment:error',
					handleWindowPaymentError
				);
			};
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReady, eventBus, paymentTimeout, hideOnEsntialPaymentError]);

	useEffect(() => {
		// update ref to current value for timeout
		paymentsRef.current = udsPayments;

		// update payments data
		if (udsPayments && Object.keys(udsPayments).length > 0) {
			// esntial data model uses 'rates' plural
			// uds data model uses 'rate' singular
			const rateKey = udsPayments?.rate ? 'rate' : 'rates';

			// Check if the rate key exists and has data
			if (
				!udsPayments[rateKey] ||
				typeof udsPayments[rateKey] !== 'object'
			) {
				logger.warn(
					'WS-TRACK-MY-OFFER: Payment data missing rate/rates structure',
					{
						udsPayments,
						rateKey,
						hasRate: !!udsPayments[rateKey]
					}
				);
				return;
			}

			const creditRatingKey = Object.keys(udsPayments[rateKey])[0];
			if (!creditRatingKey) {
				logger.warn('WS-TRACK-MY-OFFER: No credit rating key found', {
					rateKey,
					rateData: udsPayments[rateKey]
				});
				return;
			}

			const paymentTypes = udsPayments[rateKey][creditRatingKey];
			if (!paymentTypes || typeof paymentTypes !== 'object') {
				logger.warn('WS-TRACK-MY-OFFER: No payment types found', {
					creditRatingKey,
					paymentTypes
				});
				return;
			}
			const supportedDealTypes = udsPayments.supportedDealTypes || [];
			const actualPaymentKeys = Object.keys(paymentTypes);

			// Use supported deal types if available, to display payment options with error states
			// otherwise use all available payment keys
			const paymentKeys =
				supportedDealTypes.length > 0
					? supportedDealTypes
							.map((dealType) => {
								// use nationalOffers for finance if available
								if (
									dealType === 'finance' &&
									actualPaymentKeys.includes('nationalOffers')
								) {
									return 'nationalOffers';
								}
								return DEAL_TYPE_CONFIG[dealType];
							})
							.filter(Boolean) // Remove any undefined values
					: actualPaymentKeys;

			const formattedPayments = paymentKeys
				.map((key) => {
					const config =
						PAYMENT_TYPE_CONFIG[key] ||
						PAYMENT_TYPE_CONFIG.paymentOffers;
					const { label: paymentLabel, displayIndex } = config;

					// isFinanceErrorState, isLeaseErrorState
					const errorKey = `is${paymentLabel}ErrorState`;

					// Handle payment types with error states
					if (udsPayments[errorKey]) {
						logger.warn(
							`WS-TRACK-MY-OFFER: payment type has error ${key}`
						);
						return {
							payment: 0,
							paymentLabel,
							hasError: true,
							key: `paymentOption_${key}`,
							displayIndex,
							isSelected:
								udsPayments?.dealType ===
								paymentLabel.toLowerCase()
						};
					}

					// Check if payment type exists and has data
					if (
						!paymentTypes[key] ||
						typeof paymentTypes[key] !== 'object'
					) {
						logger.warn(
							`WS-TRACK-MY-OFFER: No data for payment type ${key}`
						);
						return null;
					}

					const paymentTermKeys = Object.keys(paymentTypes[key]);
					if (paymentTermKeys.length === 0) {
						logger.warn(
							`WS-TRACK-MY-OFFER: No payment terms for ${key}`
						);
						return null;
					}

					const paymentTermKey = paymentTermKeys[0];
					const paymentTermData = paymentTypes[key][paymentTermKey];

					// Check if payment term data exists and is an array
					if (
						!Array.isArray(paymentTermData) ||
						paymentTermData.length === 0
					) {
						logger.warn(
							`WS-TRACK-MY-OFFER: No payment data array for ${key}/${paymentTermKey}`
						);
						return null;
					}

					const paymentTypeData = paymentTermData[0];
					// For cash offers, use cashAmount; for others use payment or fallback to salePrice
					const payment = !udsPayments[errorKey]
						? (key === 'cashOffers' &&
						  flags['ws-track-my-offer-preload-payment-data']
								? paymentTypeData?.cashAmount
								: paymentTypeData?.payment) ||
						  paymentTypeData?.pricingDetails?.salePrice
						: 0;

					// Log payment calculation for debugging
					if (key === 'cashOffers') {
						logger.info(
							'WS-TRACK-MY-OFFER: Cash offer payment calculation',
							{
								key,
								cashAmount: paymentTypeData?.cashAmount,
								payment: paymentTypeData?.payment,
								salePrice:
									paymentTypeData?.pricingDetails?.salePrice,
								finalPayment: payment
							}
						);
					}

					const incentiveIds = paymentTypeData?.incentiveIds;
					const paymentTerm =
						paymentTermKey !== '-1' ? paymentTermKey : null;
					const downPayment = paymentTypeData?.downPayment;
					const aprListingType =
						listingType === 'new' ? listingType : 'used';
					const apr = paymentTypeData?.[aprListingType];

					return {
						payment: Math.floor(payment),
						paymentTerm,
						paymentLabel,
						hasSpecial:
							enableIncentives === 'true' &&
							incentiveIds !== undefined &&
							incentiveIds.length !== 0,
						incentiveIds,
						key: `paymentOption_${key}`,
						displayIndex,
						downPayment,
						apr,
						annualMiles: paymentTypeData?.annualMiles,
						hasTaxesAndFees: !!(
							paymentTypeData.taxesAndFees ||
							paymentTypeData.taxesFees
						),
						isSelected:
							udsPayments?.dealType === paymentLabel.toLowerCase()
					};
				})
				// Filter out any null values from failed mappings
				.filter((payment) => payment !== null)
				// payment selector displays as Finance, Lease and Cash
				.sort((a, b) => a.displayIndex - b.displayIndex);

			// Only process if we have valid payments
			if (formattedPayments.length > 0) {
				const finalPayments = formattedPayments.map(
					(payment, index) => {
						return {
							...payment,
							firstItem: index === 0,
							lastItem: index === formattedPayments.length - 1
						};
					}
				);
				setPaymentsData(finalPayments);
			} else {
				logger.warn('WS-TRACK-MY-OFFER: No valid payment data found');
				setPaymentsData([]);
				setHasError(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [udsPayments, enableIncentives, listingType]);

	return [isLoading, hasError, paymentsData];
};

export default usePaymentData;
